import React, { useCallback, useMemo } from 'react'
import { Formik } from 'formik'
import { ValidationSchemaProvider } from 'context/validationSchema'
import { useCustomFields } from 'context/customFields'
import { setCustomFieldValue } from 'services/CustomFieldService'
import {
  getPropertyNameFromType,
  mapCustomFieldsToInitialValues,
  mapCustomFieldsToSchema
} from 'components/Shared/FormikForm/FormikFormWithCustomFields/utils'

export function FormikFormWithCustomFields ({ onSubmit, initialValues, baseValidationSchema, children, ...rest }) {
  const { customFields } = useCustomFields()

  const customFieldsDefinitions = useMemo(() => customFields ? Object.keys(customFields)
    .reduce((acc, key) => acc.concat(customFields[key]), []) : [], [customFields])

  const validationSchemaWithCustomFields = useMemo(() =>
    baseValidationSchema.concat(mapCustomFieldsToSchema(customFieldsDefinitions)), [baseValidationSchema, customFieldsDefinitions])

  const initialValuesWithCustomFields = useMemo(() => customFields &&
    { ...initialValues, ...mapCustomFieldsToInitialValues(customFieldsDefinitions) },
    [initialValues, customFieldsDefinitions, customFields])

  const setCustomFieldValues = useCallback((definitions, customFields) => entityId => {
      return Promise.all(definitions.map(definition =>
        setCustomFieldValue(definition.id, entityId, {
          value: {
            [getPropertyNameFromType(definition.type)]: customFields[definition.id]
          }
        })))
    }, []
  )

  const handleSubmit = useCallback(({ customFields, ...values }) => {
      const customFieldsDefinitionsWithAssignedValues = customFieldsDefinitions.filter(definition => customFields[definition.id] !== undefined)
      onSubmit(values, setCustomFieldValues(customFieldsDefinitionsWithAssignedValues, customFields))
    }, [onSubmit, setCustomFieldValues, customFieldsDefinitions]
  )

  return <ValidationSchemaProvider schema={validationSchemaWithCustomFields}>
    {initialValuesWithCustomFields && validationSchemaWithCustomFields && <Formik
      onSubmit={handleSubmit}
      initialValues={initialValuesWithCustomFields}
      validationSchema={validationSchemaWithCustomFields}
      {...rest}>
      {children}
    </Formik>
    }
  </ValidationSchemaProvider>
}
